// src/helpers/api_helper.js
import axios from "axios";

// Use your environment variable for base URL
const baseURL = process.env.REACT_APP_BASE_URL 

// Get auth token from localStorage (CHANGED FROM sessionStorage)
const getAuthToken = () => {
  return (
    localStorage.getItem("authToken") || localStorage.getItem("token") || null
  );
};

// Create axios instance for authenticated requests
const authenticatedAxios = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Create axios instance for public requests (login)
const publicAxios = axios.create({
  baseURL: baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to authenticated requests only
authenticatedAxios.interceptors.request.use(
  function (config) {
    const token = getAuthToken();
    if (token) {
      const cleanToken = token.replace(/"/g, "");
      // Set Authentication-Token header (as shown in your image)
      config.headers["Authentication-Token"] = cleanToken;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

// Handle errors for both instances
const errorHandler = (error) => {
  let message;
  switch (error.response?.status) {
    case 500:
      message = "Internal Server Error";
      break;
    case 401:
      message = "Invalid credentials or session expired";
      // Clear all auth-related localStorage (CHANGED FROM sessionStorage)
      localStorage.removeItem("authToken");
      localStorage.removeItem("authUser");
      localStorage.removeItem("token");
      localStorage.removeItem("userDetails");
      localStorage.removeItem("dismissedAlerts");
      localStorage.removeItem("pendingShortCode");
      localStorage.removeItem("pendingShortCodeUrl");
      // Redirect to login
      window.location.href = "/login";
      break;
    case 403:
      message = "Access denied";
      break;
    case 404:
      message = "Data not found";
      break;
    default:
      message =
        error.response?.data?.message ||
        error.response?.data?.non_field_errors?.[0] ||
        error.message ||
        "An error occurred";
  }
  return Promise.reject(message);
};

// Add error handling to both instances
authenticatedAxios.interceptors.response.use(
  (response) => (response.data ? response.data : response),
  errorHandler
);

publicAxios.interceptors.response.use(
  (response) => (response.data ? response.data : response),
  errorHandler
);

// APIClient class with constructor to choose between authenticated and public
class APIClient {
  constructor(useAuth = true) {
    // Choose axios instance based on constructor parameter
    this.axios = useAuth ? authenticatedAxios : publicAxios;
  }

  get = (url, params) => {
    let paramKeys = [];

    if (params) {
      Object.keys(params).forEach((key) => {
        if (params[key] !== undefined && params[key] !== null) {
          paramKeys.push(key + "=" + encodeURIComponent(params[key]));
        }
      });

      const queryString = paramKeys.length ? paramKeys.join("&") : "";
      return this.axios.get(`${url}?${queryString}`);
    } else {
      return this.axios.get(url);
    }
  };

  create = (url, data) => {
    return this.axios.post(url, data);
  };

  update = (url, data) => {
    return this.axios.patch(url, data);
  };

  put = (url, data) => {
    return this.axios.put(url, data);
  };

  delete = (url, config) => {
    return this.axios.delete(url, { ...config });
  };
}

// Function to set authorization token (CHANGED TO USE localStorage)
const setAuthorization = (token) => {
  if (token) {
    const cleanToken = token.replace(/"/g, "");
    // Store the token in localStorage (CHANGED FROM sessionStorage)
    localStorage.setItem("authToken", cleanToken);
    // Set default header for authenticated axios instance
    authenticatedAxios.defaults.headers.common["Authentication-Token"] =
      cleanToken;
  } else {
    // Clear token when null is passed
    localStorage.removeItem("authToken");
    delete authenticatedAxios.defaults.headers.common["Authentication-Token"];
  }
};

// UPDATED: Get logged in user from localStorage
const getLoggedinUser = () => {
  try {
    const user =
      localStorage.getItem("authUser") || localStorage.getItem("userDetails");
    if (!user) {
      return null;
    }
    return JSON.parse(user);
  } catch (error) {
    console.error("Error parsing logged in user:", error);
    return null;
  }
};

// ADDED: Initialize authorization on app startup
export const initializeAuth = () => {
  const token = getAuthToken();
  if (token) {
    setAuthorization(token);
    return token;
  }
  return null;
};

export { APIClient, setAuthorization, getLoggedinUser, getAuthToken };
